import React, { useEffect } from "react";
import { Navigate, redirect, useLocation } from "react-router-dom";
import useAuthStore from "../../stores/auth-store.js";
import ExtraError from "../../pages/extra/extra-error";

/**
 * Component to protect routes that require authentication and/or specific permissions
 * If user is not authenticated, redirects to login page
 * If user lacks required permissions, shows error page
 */
const ProtectedRoute = ({
  children,
  requiredPermissions = [],
  requireAll = false,
}) => {
  const location = useLocation();
  const {
    isAuthenticated,
    syncAuthState,
    isLoading,
    hasViewPermission,
    hasAnyViewPermission,
    setRedirect,
    isLoggedOut,
    redirectTo,
  } = useAuthStore();

  // Check authentication status on mount and when route changes
  useEffect(() => {
    syncAuthState();
  }, [syncAuthState, location.pathname]);

  // Store redirect for unauthenticated access attempts
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !isLoggedOut && !redirectTo) {
      setRedirect(location.pathname, location.state, "unauthorized");
    }
  }, [
    isAuthenticated,
    isLoading,
    isLoggedOut,
    redirectTo,
    location.pathname,
    location.state,
    setRedirect,
  ]);

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/user/login"
        state={{
          from: location,
          reason: "unauthorized",
        }}
        replace
      />
    );
  }

  // Check permissions if required
  if (requiredPermissions.length > 0) {
    const hasAccess = requireAll
      ? requiredPermissions.every((permission) => hasViewPermission(permission))
      : hasAnyViewPermission(requiredPermissions);

    if (!hasAccess) {
      return <ExtraError />;
    }
  }

  // Render the protected content if authenticated and authorized
  return children;
};

export default ProtectedRoute;
